import chargeModelApi from 'api/chargeModelApi';
import AgencyHelper, { AgencyConstants } from 'features/Agency/agencyHelper';
import ChargeHelper from 'features/Charge/chargeHelper';
import SystemHelper from 'features/System/systemHelper';
import KTFormInput, {
    KTFormInputType,
} from 'general/components/OtherKeenComponents/Forms/KTFormInput';
import KTBSDropdown from 'general/components/OtherKeenComponents/KTBSDropdown';
import KTRadio, {
    KTRadioType,
} from 'general/components/OtherKeenComponents/KTFormControls/KTRadio';
import CustomDropdown2 from 'general/components/Others/CustomDropdown2';
import ImageView from 'general/components/quybka/Images/ImageView';
import AppColors from 'general/constants/AppColors';
import miscHelper from 'general/helpers/miscHelper';
import ToastHelper from 'general/helpers/ToastHelper';
import UIHelper from 'general/helpers/UIHelper';
import Utils from 'general/utils/Utils';
import _ from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import TechnicalAcceptanceProductTableView from '../TechnicalAcceptanceProductTableView';
import { AppDialogHelper } from 'general/components/AppDialog';
import AppResource from 'general/constants/AppResource';

AgencySaleCreateTechnicalAcceptanceView.propTypes = {
    title: PropTypes.string,
    contentEl: PropTypes.element,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func,
    saleDetail: PropTypes.object,
    defaultAcceptanceType: PropTypes.string,
    acceptanceDetail: PropTypes.object,
    onSetDialogSize: PropTypes.func,
    chargeStationDetail: PropTypes.object,
};

function AgencySaleCreateTechnicalAcceptanceView({
    title = '',
    contentEl = null,
    onCancel,
    onConfirm,
    saleDetail,
    defaultAcceptanceType = '',
    acceptanceDetail = null,
    onSetDialogSize = () => { },
    chargeStationDetail = null,
}) {
    // MARK: ---- PROPERTIES ----
    const [acceptanceDate, setAcceptanceDate] = useState(moment().format('YYYY-MM-DD'));
    const [acceptanceNumber, setAcceptanceNumber] = useState('');
    const [validFrom, setValidFrom] = useState(moment().format('YYYY-MM-DD'));
    const [validTo, setValidTo] = useState(moment().add(10, 'year').format('YYYY-MM-DD'));
    const [selectedFiles, setSelectedFiles] = useState([]);
    const selectFilePathList = useRef([]);
    const [acceptanceType, setAcceptanceType] = useState(AgencyConstants.AcceptanceType.DIGITAL);
    const [paperAcceptanceDocumentFile, setPaperAcceptanceDocumentFile] = useState(null);
    const [selectedChargeStation, setSelectedChargeStation] = useState(null);
    const [selectedAgencySale, setSelectedAgencySale] = useState(null);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [readyToValidate, setReadyToValidate] = useState(false);
    const [loading, setLoading] = useState(false);
    const [selectedChargeModelList, setSelectedChargeModelList] = useState([
        {
            tax: 0,
            name: '',
            note: '',
            type: '',
            unit: 'Trụ',
            price: 0,
            amount: 0,
            taxRate: 0,
            quantity: 1,
            modelId: null,
            modelCode: null,
            amountBeforeTax: 0
        }
    ]);
    const [selectedElectricityList, setSelectedElectricityList] = useState([]);
    const [selectedLightElectricityList, setSelectedLightElectricityList] = useState([]);
    const [selectedGroundSystemList, setSelectedGroundSystemList] = useState([]);
    const [selectedPCCCList, setSelectedPCCCList] = useState([]);
    const [selectedBuildList, setSelectedBuildList] = useState([]);
    const [selectedOtherList, setSelectedOtherList] = useState([]);

    const isDisabled = useMemo(() => {
        return acceptanceDetail &&
            [
                AgencyConstants.AcceptanceStatus.ADMIN_SIGNED,
                AgencyConstants.AcceptanceStatus.AGENCY_SIGNED,
                AgencyConstants.AcceptanceStatus.COMPLETED,
            ].includes(acceptanceDetail?.status);
    }, [acceptanceDetail])

    const isDigitalType = useMemo(() => {
        return acceptanceType === AgencyConstants.AcceptanceType.DIGITAL;
    }, [acceptanceType])

    useEffect(() => {
        if (!!chargeStationDetail) {
            setSelectedChargeStation({
                id: chargeStationDetail?.stationId,
                name: chargeStationDetail?.name,
                item: chargeStationDetail
            });
        }
    }, [chargeStationDetail])

    useEffect(() => {
        if (!!saleDetail) {
            setSelectedAgencySale({
                id: saleDetail?.saleId,
                name: saleDetail?.saleCode,
                item: saleDetail
            });
        }
    }, [saleDetail])

    useEffect(() => {
        if (onSetDialogSize) {
            onSetDialogSize(acceptanceType);
        }
    }, [acceptanceType])

    useEffect(() => {
        const getAcceptanceNumber = async () => {
            if (!acceptanceDetail) {
                const number = await AgencyHelper.API.requestGenerateAcceptanceNumber({ acceptanceType: 'TECHNICAL' });
                if (number) {
                    setAcceptanceNumber(number);
                }
            } else {
                setAcceptanceNumber(acceptanceDetail?.number)
            }
        };
        getAcceptanceNumber();
    }, []);

    useEffect(() => {
        const getChargeStationDetail = async () => {
            if (acceptanceDetail?.stationId) {
                const data = await ChargeHelper.API.getChargeStationDetail(acceptanceDetail?.stationId);
                if (data) {
                    setSelectedChargeStation({
                        id: data?.stationId,
                        name: data?.name,
                        item: data
                    });
                }
            }
        }
        getChargeStationDetail();
    }, [acceptanceDetail])

    useEffect(() => {
        const getAgencySaleDetail = async () => {
            if (acceptanceDetail?.saleId) {
                const data = await AgencyHelper.API.requestGetSaleDetail(acceptanceDetail?.saleId);
                if (data) {
                    setSelectedAgencySale({
                        id: data?.saleId,
                        name: data?.saleCode,
                        item: data
                    });
                }
            }
        }
        getAgencySaleDetail();
    }, [acceptanceDetail])

    useEffect(() => {
        const getStaffDetail = async () => {
            if (acceptanceDetail?.userId) {
                const { users } = await SystemHelper.API.User.requestGetUserList({ userId: acceptanceDetail?.userId });
                if (users) {
                    setSelectedStaff(_.first(users));
                }
            }
        }
        getStaffDetail();
    }, [acceptanceDetail])

    useEffect(() => {
        if (acceptanceDetail) {
            setAcceptanceNumber(acceptanceDetail?.number);
            setSelectedFiles(acceptanceDetail?.attachedFiles ?? []);
            setAcceptanceType(acceptanceDetail?.documentType ?? []);
            setPaperAcceptanceDocumentFile(acceptanceDetail?.document);
            setAcceptanceDate(
                acceptanceDetail?.createdAt
                    ? Utils.formatDateTimeToString(acceptanceDetail?.createdAt, 'YYYY-MM-DD')
                    : ''
            );
            setValidFrom(
                acceptanceDetail?.time
                    ? Utils.formatDateTimeToString(acceptanceDetail?.time, 'YYYY-MM-DD')
                    : ''
            );
        }
    }, [acceptanceDetail]);

    useEffect(() => {
        if (!acceptanceDetail) {
            if (selectedAgencySale && selectedAgencySale?.item?.items?.length) {
                let items = selectedAgencySale?.item?.items?.map((item) => {
                    return {
                        tax: item?.tax,
                        name: item?.chargeModel?.name,
                        unit: 'Trụ',
                        price: item?.amountBeforeTax,
                        taxRate: item?.taxRate,
                        modelId: item?.modelId,
                        modelCode: item?.chargeModel?.modelCode,
                        type: item?.chargeModel?.type,
                        amount: item?.amount,
                        amountBeforeTax: item?.amountBeforeTax,
                        note: "",
                    };
                });
                items = items?.reduce((acc, item) => {
                    const existingItem = acc.find((i) => i.modelId === item.modelId && i.price === item.price);
                    if (existingItem) {
                        existingItem.quantity += 1;
                        existingItem.tax += item?.tax;
                        existingItem.amountBeforeTax += item?.amountBeforeTax;
                        existingItem.amount += item?.amount;
                    } else {
                        acc.push({ ...item, quantity: 1, amount: item?.amount, amountBeforeTax: item?.amountBeforeTax });
                    }
                    return acc;
                }, []);
                setSelectedChargeModelList(items);
            }
        } else {
            let items = acceptanceDetail?.items?.items?.map((item) => {
                return {
                    tax: item?.tax,
                    name: item?.name,
                    unit: 'Trụ',
                    price: item?.price,
                    taxRate: item?.taxRate,
                    modelId: item?.modelId,
                    modelCode: item?.modelCode,
                    type: item?.type,
                    amount: item?.amount,
                    amountBeforeTax: item?.amountBeforeTax,
                    note: item?.note,
                    quantity: item?.quantity,
                };
            });
            setSelectedChargeModelList(items);
        }
    }, [selectedAgencySale, acceptanceDetail])

    useEffect(() => {
        const handleSetInventoryItems = (categoryType, setData) => {
            const data = acceptanceDetail?.items?.inventoryItems?.filter(
                (item) => item?.categoryType === categoryType
            ) ?? [];
            setData(data);
        };

        if (acceptanceDetail?.items?.inventoryItems) {
            handleSetInventoryItems('CAPDIEN', setSelectedElectricityList);
            handleSetInventoryItems('DIENNHE', setSelectedLightElectricityList);
            handleSetInventoryItems('TIEPDIA', setSelectedGroundSystemList);
            handleSetInventoryItems('PCCC', setSelectedPCCCList);
            handleSetInventoryItems('XAYDUNG', setSelectedBuildList);
            handleSetInventoryItems('KHAC', setSelectedOtherList);
        }
    }, [acceptanceDetail]);

    useEffect(() => {
        if (defaultAcceptanceType?.length) {
            setAcceptanceType(defaultAcceptanceType);
        }
    }, [defaultAcceptanceType])

    const [categoryFilter, setCategoryFilter] = useState();
    const [categoriesList, setCategoriesList] = useState([]);
    const [categoriesTotal, setCategoriesTotal] = useState(0);

    const didMountFetch = useRef(false);

    const handleGetCategoriesList = async (params = {}) => {
        try {
            const nextPage = params?.page ?? 0;
            const query = params?.q ?? '';
            if (
                !categoryFilter && !didMountFetch.current ||
                categoryFilter && (
                    query !== categoryFilter?.q ||
                    nextPage > categoryFilter?.page
                )
            ) {
                didMountFetch.current = true;
                setCategoryFilter({ page: nextPage, q: query });

                const data = await ChargeHelper.API.requestGetChargeInventoryCategoryList({
                    page: nextPage,
                    limit: 10,
                    q: query,
                });

                const { categories = [], total = 0 } = data ?? {};

                setCategoriesList((prev) => [...(prev ?? []), ...categories]);
                setCategoriesTotal(total);
            }
        } catch (err) {
            console.error('Lỗi khi fetch category list:', err);
        }
    };

    const dropdownChargeStationEl = useMemo(() => {
        return (
            <CustomDropdown2
                id="stationId"
                name="stationId"
                toggleElement={
                    <button
                        className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between 
                  ${(!!chargeStationDetail || isDisabled) ? 'dropdown-disabled' : ''}
                  ${readyToValidate && !Utils.isValid(selectedChargeStation) ? 'button-invalid' : ''}
                  `}
                        type="button"
                    >
                        {selectedChargeStation?.name ?? 'Chọn trạm sạc'}
                    </button>
                }
                selectedValue={selectedChargeStation}
                subTitleType="address"
                dropdownMenuItems={[{ name: 'Trạm sạc', role: 'STATION' }]}
                getSubItems={async (item) => {
                    return ChargeHelper.API.requestGetChargeStationList(Utils.filterParams({
                        page: item?.page || 0,
                        limit: 10,
                        q: item?.q || '',
                        agencyId: (saleDetail?.orderType === 'INVESTOR' || saleDetail?.orderType === 'COINVESTOR') ? saleDetail?.buyerId : '',
                        premiseId: saleDetail?.orderType === 'LANDLORD' ? saleDetail?.premiseId : '',
                    })).then((data) => {
                        return {
                            values: data.chargeStations?.map((station) => ({
                                id: station?.stationId,
                                name: station?.name,
                                item: station,
                                address: ChargeHelper.Utils.getChargeStationFullAddress(station),
                            })),
                            total: data.total,
                        };
                    });
                }}
                dropdownMenuStyle={{ maxHeight: '30vh' }}
                dropdownMenuClassName="overflow-auto w-100"
                onChange={(value) => {
                    setSelectedChargeStation(value);
                }}
                isDropdownForm={true}
                titleSearchable={true}
                searchable={true}
                searchFormClassName="mx-3 my-1 search-form-border"
                ignoreConfirmClick={true}
                defaultValue={{
                    name: 'Trạm sạc',
                    role: 'STATION',
                }}
                disabled={!!chargeStationDetail || isDisabled}
                footerEl={
                    <div
                        className="p-4 position-sticky sticky-bottom bg-white"
                        style={{
                            borderTop: `1px solid ${AppColors.gray[300]}`,
                        }}
                    >
                        {UIHelper.buildNegativeButton('Bỏ chọn', () => {
                            setSelectedChargeStation(null);
                        })}
                    </div>
                }
            />
        )
    }, [selectedChargeStation, chargeStationDetail, isDisabled])

    const dropdownAgencySaleEl = useMemo(() => {
        return (
            <CustomDropdown2
                id="saleId"
                name="saleId"
                toggleElement={
                    <button
                        className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between 
                  ${!!saleDetail ? 'dropdown-disabled' : ''}
                  ${readyToValidate && !Utils.isValid(selectedAgencySale) ? 'button-invalid' : ''}
                  `}
                        type="button"
                    >
                        {selectedAgencySale?.name ?? 'Chọn đơn hàng'}
                    </button>
                }
                selectedValue={selectedAgencySale}
                dropdownMenuItems={[{ name: 'Đơn hàng', role: 'ORDER' }]}
                getSubItems={async (item) => {
                    return AgencyHelper.API.requestGetSaleList({
                        page: item?.page || 0,
                        limit: 10,
                        q: item?.q || '',
                        orderType: 'INVESTOR, LANDLORD',
                        status: 'INSTALLED',
                        hasAcceptanceReport: 0,
                    }).then((data) => {
                        return {
                            values: data.sales?.map((sale) => ({
                                id: sale?.saleId,
                                name: sale?.saleCode,
                                item: sale,
                            })),
                            total: data.total,
                        };
                    });
                }}
                dropdownMenuStyle={{ maxHeight: '30vh' }}
                dropdownMenuClassName="overflow-auto w-100"
                onChange={(value) => {
                    const isValidAgencySale = handleCheckSelectedAgencySale(value?.item);
                    if (isValidAgencySale) {
                        setSelectedAgencySale(value);
                    }
                }}
                isDropdownForm={true}
                titleSearchable={true}
                searchable={true}
                searchFormClassName="mx-3 my-1 search-form-border"
                ignoreConfirmClick={true}
                defaultValue={{
                    name: 'Đơn hàng',
                    role: 'ORDER',
                }}
                disabled={!!saleDetail}
                footerEl={
                    <div
                        className="p-4 position-sticky sticky-bottom bg-white"
                        style={{
                            borderTop: `1px solid ${AppColors.gray[300]}`,
                        }}
                    >
                        {UIHelper.buildNegativeButton('Bỏ chọn', () => {
                            setSelectedAgencySale(null);
                        })}
                    </div>
                }
            />
        )
    }, [selectedAgencySale, saleDetail])

    const dropdownStaffEl = useMemo(() => {
        return (
            <CustomDropdown2
                id="userId"
                name="userId"
                toggleElement={
                    <button
                        className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between 
                  ${readyToValidate && !Utils.isValid(selectedStaff) ? 'button-invalid' : ''}
                  `}
                        type="button"
                    >
                        {selectedStaff?.fullname ?? 'Chọn kỹ thuật viên'}
                    </button>
                }
                selectedValue={selectedStaff}
                // subTitleType="address"
                dropdownMenuItems={[{ name: 'Kỹ thuật viên', role: 'STAFF' }]}
                getSubItems={async (item) => {
                    return SystemHelper.API.User.requestGetUserList({
                        page: item?.page || 0,
                        limit: 10,
                        role: 'ADMIN',
                        position: "INFRA_ENGINEERING_MANAGER, INFRA_ENGINEER",
                        q: item?.q || '',
                    }).then((data) => {
                        return {
                            values: data?.users?.map((user) => ({
                                id: user?.userId,
                                name: user?.fullname,
                                fullname: user?.fullname,
                                item: user,
                            })),
                            total: data.total,
                        };
                    });
                }}
                dropdownMenuStyle={{ maxHeight: '30vh' }}
                dropdownMenuClassName="overflow-auto w-100"
                onChange={(value) => {
                    setSelectedStaff(value);
                }}
                isDropdownForm={true}
                titleSearchable={true}
                searchable={true}
                searchFormClassName="mx-3 my-1 search-form-border"
                ignoreConfirmClick={true}
                defaultValue={{
                    name: 'Kỹ thuật viên',
                    role: 'STAFF',
                }}
                // disabled={!editMode}
                footerEl={
                    <div
                        className="p-4 position-sticky sticky-bottom bg-white"
                        style={{
                            borderTop: `1px solid ${AppColors.gray[300]}`,
                        }}
                    >
                        {UIHelper.buildNegativeButton('Bỏ chọn', () => {
                            setSelectedStaff(null);
                        })}
                    </div>
                }
            />
        )
    }, [selectedStaff, isDisabled])

    const [chargeBrandList, setChargeBrandList] = useState([]);
    useEffect(() => {
        ChargeHelper.API.requestGetChargeBrandList({}).then((data) => {
            const { brands } = data;
            setChargeBrandList(brands);
        });
    }, []);
    const chargeBrandOptions = useMemo(() => {
        return chargeBrandList?.map((row) => {
            return {
                name: row?.name,
                role: row?.brandId,
            };
        });
    }, [chargeBrandList]);

    const chargeModelListTable = useMemo(() => (
        <div className='rounded-lg'
            style={{
                border: `1px solid ${AppColors.gray[300]}`,
            }}>
            <div className="table-responsive">
                <table className="table mb-0">
                    <thead>
                        <tr>
                            {[
                                { label: 'Sản phẩm', isRequired: true, className: 'pl-3 rounded-top-left-lg' },
                                // { label: 'Quy cách kỹ thuật', isRequired: true },
                                // { label: 'Hãng sản xuất', isRequired: false },
                                { label: 'Đơn vị tính', isRequired: false },
                                { label: 'Số lượng', isRequired: true },
                                { label: 'Ghi chú', isRequired: false },
                                { label: '', className: 'pr-3 w-40px rounded-top-right-lg' },
                            ].map((col, idx) => (
                                <th
                                    key={idx}
                                    className={col.className ?? ''}
                                    style={{
                                        ...UIHelper.buildFontStyle('600', '12px', AppColors.gray[900]),
                                        backgroundColor: AppColors.gray[100],
                                        borderBottom: `1px solid ${AppColors.gray[300]}`,
                                        borderRight: idx !== 4 && `1px solid ${AppColors.gray[300]}`,
                                    }}
                                >
                                    {UIHelper.buildFormLabel(col.label, col.isRequired)}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {selectedChargeModelList?.map((item, index) => {
                            return (
                                <tr key={`MODEL_${index}`} className="w-100">
                                    <td
                                        className="border-0 pl-3 mb-0 pb-0 pr-0 w-50"
                                        style={{
                                            minWidth: '300px',
                                        }}
                                    >
                                        <CustomDropdown2
                                            ignoreConfirmClick={true}
                                            name={`MODEL_${index}`}
                                            id={`MODEL_${index}`}
                                            toggleElement={
                                                <button
                                                    className={`btn btn-white border-secondary dropdown-toggle w-100 d-flex align-items-center justify-content-between
                                                        ${isDisabled ? 'disabled' : ''}
                                                        `}
                                                    type="button"
                                                >
                                                    {item?.modelId
                                                        ? `[${item?.type}] ${item?.name}`
                                                        : 'Chọn dòng trụ sạc'}
                                                </button>
                                            }
                                            selectedValue={item}
                                            dropdownMenuItems={chargeBrandOptions}
                                            dropdownMenuClassName="overflow-auto w-100 position-relative"
                                            dropdownMenuStyle={{ maxHeight: '30vh' }}
                                            titleSearchable={true}
                                            defaultTitle="Chọn hãng sản xuất trụ sạc"
                                            getSubItems={async (item) => {
                                                return chargeModelApi.getChargeModelList(
                                                    Utils.filterParams({
                                                        brandId: item?.role,
                                                        page: item?.page || 0,
                                                        limit: 10,
                                                        q: item?.q || '',
                                                    })
                                                ).then((data) => {
                                                    let values = data.models?.map((row) => ({
                                                        id: row?.modelId,
                                                        name: `[${row?.type}] ${row?.name}`,
                                                        item: row,
                                                    }));
                                                    return {
                                                        values: values,
                                                        total: data?.total,
                                                    };
                                                });
                                            }}
                                            onChange={(value) => {
                                                const item = value?.item;
                                                const tmpChargeModelList = [
                                                    ...selectedChargeModelList,
                                                ];
                                                tmpChargeModelList[index] = {
                                                    ...tmpChargeModelList[index],
                                                    tax: 0,
                                                    name: item?.name,
                                                    price: 0,
                                                    taxRate: 0,
                                                    modelId: item?.modelId,
                                                    modelCode: item?.modelCode,
                                                    type: item?.type,
                                                };
                                                setSelectedChargeModelList(tmpChargeModelList);
                                            }}
                                            isDropdownForm={true}
                                            searchable={true}
                                            searchFormClassName="mx-3 my-1 search-form-border"
                                            disabled={isDisabled}
                                        />

                                    </td>
                                    {/* <td
                                        className="border-0 mb-0 pb-0 pr-0"
                                        style={{
                                            minWidth: '120px',
                                        }}
                                    >
                                        <KTFormInput
                                            name={`MODEL_${item?.modelId ?? index}_description`}
                                            placeholder="Nhập mô tả"
                                            type={KTFormInputType.text}
                                            showUnit={false}
                                            value={item?.description}
                                            onChange={(value) => {
                                                const tmpChargeModelList = [
                                                    ...selectedChargeModelList,
                                                ];
                                                tmpChargeModelList[index].description = value;
                                                setSelectedChargeModelList(tmpChargeModelList);
                                            }}
                                        />
                                    </td>
                                    <td
                                        className="border-0 mb-0 pb-0 pr-0"
                                        style={{
                                            minWidth: '80px',
                                        }}
                                    >
                                        <KTFormInput
                                            name={`MODEL_${item?.modelId ?? index}_brandId`}
                                            placeholder="Nhập tên"
                                            type={KTFormInputType.text}
                                            showUnit={false}
                                            value={_.find(chargeBrandOptions, (brand) => brand?.role === item?.brandId)?.name ?? ''}
                                            onChange={(value) => {
                                                const tmpChargeModelList = [
                                                    ...selectedChargeModelList,
                                                ];
                                                tmpChargeModelList[index].quantity = value;
                                                setSelectedChargeModelList(tmpChargeModelList);
                                            }}
                                            disabled={true}
                                        />
                                    </td> */}
                                    <td
                                        className="border-0 mb-0 pb-0 pr-0"
                                        style={{
                                            width: '15%',
                                            minWidth: '80px',
                                        }}
                                    >
                                        <KTFormInput
                                            name={`MODEL_${item?.modelId ?? index}_unit`}
                                            placeholder="Nhập đơn vị tính"
                                            type={KTFormInputType.text}
                                            showUnit={false}
                                            value={item?.unit}
                                            onChange={() => { }}
                                            disabled={true}
                                        />
                                    </td>
                                    <td
                                        className="border-0 mb-0 pb-0 pr-0"
                                        style={{
                                            width: '10%',
                                            minWidth: '80px',
                                        }}
                                    >
                                        <KTFormInput
                                            name={`MODEL_${item?.modelId ?? index}_quantity`}
                                            placeholder="Nhập số lượng"
                                            type={KTFormInputType.currency}
                                            showUnit={false}
                                            value={item?.quantity}
                                            onChange={(value) => {
                                                const updated = [...selectedChargeModelList];
                                                const currentItem = updated[index];

                                                updated[index] = {
                                                    ...currentItem,
                                                    quantity: Number(value ?? 0),
                                                    amountBeforeTax: Math.round((currentItem?.price ?? 0) * (value ?? 0)),
                                                    tax: Math.round((currentItem?.price ?? 0) * (value ?? 0) * (currentItem?.taxRate ?? 0)),
                                                    amount: Math.round((currentItem?.price ?? 0) * (value ?? 0) * (1 + (currentItem?.taxRate ?? 0))),
                                                };

                                                setSelectedChargeModelList(updated);
                                            }}
                                            disabled={isDisabled}
                                        />
                                    </td>
                                    <td
                                        className="border-0 mb-0 pb-0 pr-0"
                                        style={{
                                            width: '20%',
                                            minWidth: '160px',
                                        }}
                                    >
                                        <KTFormInput
                                            name={`MODEL_${item?.modelId ?? index}_note`}
                                            placeholder="Nhập ghi chú"
                                            type={KTFormInputType.text}
                                            value={item?.note}
                                            onChange={(value) => {
                                                const tmpChargeModelList = [
                                                    ...selectedChargeModelList,
                                                ];
                                                tmpChargeModelList[index].note = value;
                                                setSelectedChargeModelList(tmpChargeModelList);
                                            }}
                                            disabled={isDisabled}
                                        />
                                    </td>
                                    <td className="border-0 pr-3 d-flex flex-column align-items-center justify-content-end mb-0 pb-0">
                                        {UIHelper.buildNegativeButton(
                                            null,
                                            isDisabled
                                                ? null
                                                : () => {
                                                    if (selectedChargeModelList.length === 1) {
                                                        setSelectedChargeModelList([
                                                            {
                                                                tax: 0,
                                                                name: '',
                                                                note: '',
                                                                type: '',
                                                                unit: 'Trụ',
                                                                price: 0,
                                                                amount: 0,
                                                                taxRate: 0,
                                                                quantity: 1,
                                                                modelId: null,
                                                                modelCode: null,
                                                                amountBeforeTax: 0
                                                            },
                                                        ]);
                                                    } else {
                                                        setSelectedChargeModelList(
                                                            _.filter(
                                                                selectedChargeModelList,
                                                                (item, i) => i !== index
                                                            )
                                                        );
                                                    }
                                                },
                                            <i className="fa-regular fa-trash-can" />
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {/* Thêm sản phẩm */}
            <div className="d-flex px-3 my-3">
                {UIHelper.buildNegativeButton(
                    'Thêm sản phẩm',
                    (isDisabled || saleDetail?.orderType === AgencyConstants.SaleOrderType.COINVESTOR)
                        ? null
                        : () => {
                            setSelectedChargeModelList([
                                ...selectedChargeModelList,
                                {
                                    tax: 0,
                                    name: '',
                                    note: '',
                                    type: '',
                                    unit: 'Trụ',
                                    price: 0,
                                    amount: 0,
                                    taxRate: 0,
                                    quantity: 1,
                                    modelId: null,
                                    modelCode: null,
                                    amountBeforeTax: 0
                                },
                            ]);
                        },
                    <i className="fa-regular fa-circle-plus" />
                )}
            </div>
        </div>
    ), [selectedChargeModelList, chargeBrandOptions])

    const electricityListTable = useMemo(() => (
        <TechnicalAcceptanceProductTableView
            dataName='CAPDIEN'
            data={selectedElectricityList}
            setData={setSelectedElectricityList}
            categoriesList={categoriesList}
            categoriesTotal={categoriesTotal}
            handleGetCategoriesList={handleGetCategoriesList}
            chargeBrandOptions={chargeBrandOptions}
            isDisabled={isDisabled}
        />
    ), [selectedElectricityList, categoriesList, categoriesTotal, chargeBrandOptions]);


    const lightElectricityListTable = useMemo(() => (
        <TechnicalAcceptanceProductTableView
            dataName='DIENNHE'
            data={selectedLightElectricityList}
            setData={setSelectedLightElectricityList}
            categoriesList={categoriesList}
            categoriesTotal={categoriesTotal}
            handleGetCategoriesList={handleGetCategoriesList}
            chargeBrandOptions={chargeBrandOptions}
            isDisabled={isDisabled}
        />
    ), [selectedLightElectricityList, categoriesList, categoriesTotal, chargeBrandOptions]);

    const groundSystemListTable = useMemo(() => (
        <TechnicalAcceptanceProductTableView
            dataName='TIEPDIA'
            data={selectedGroundSystemList}
            setData={setSelectedGroundSystemList}
            categoriesList={categoriesList}
            categoriesTotal={categoriesTotal}
            handleGetCategoriesList={handleGetCategoriesList}
            chargeBrandOptions={chargeBrandOptions}
            isDisabled={isDisabled}
        />
    ), [selectedGroundSystemList, categoriesList, categoriesTotal, chargeBrandOptions]);


    const pcccListTable = useMemo(() => (
        <TechnicalAcceptanceProductTableView
            dataName='PCCC'
            data={selectedPCCCList}
            setData={setSelectedPCCCList}
            categoriesList={categoriesList}
            categoriesTotal={categoriesTotal}
            handleGetCategoriesList={handleGetCategoriesList}
            chargeBrandOptions={chargeBrandOptions}
            isDisabled={isDisabled}
        />
    ), [selectedPCCCList, categoriesList, categoriesTotal, chargeBrandOptions]);

    const buildListTable = useMemo(() => (
        <TechnicalAcceptanceProductTableView
            dataName='XAYDUNG'
            data={selectedBuildList}
            setData={setSelectedBuildList}
            categoriesList={categoriesList}
            categoriesTotal={categoriesTotal}
            handleGetCategoriesList={handleGetCategoriesList}
            chargeBrandOptions={chargeBrandOptions}
            isDisabled={isDisabled}
        />
    ), [selectedBuildList, categoriesList, categoriesTotal, chargeBrandOptions]);

    const otherListTable = useMemo(() => (
        <TechnicalAcceptanceProductTableView
            dataName='KHAC'
            data={selectedOtherList}
            setData={setSelectedOtherList}
            categoriesList={categoriesList}
            categoriesTotal={categoriesTotal}
            handleGetCategoriesList={handleGetCategoriesList}
            chargeBrandOptions={chargeBrandOptions}
            isDisabled={isDisabled}
        />
    ), [selectedOtherList, categoriesList, categoriesTotal, chargeBrandOptions]);


    // MARK: ---- FUNCTIONS ----
    const handleFilesChanged = (e) => {
        const newFiles = Array.from(e.target.files || []);
        if (!newFiles.length) return;

        setSelectedFiles((prev) => {
            const validNew = newFiles.filter((file) => {
                if (file.name.length > 128) {
                    ToastHelper.showError(`Tên file quá dài! Tối đa cho phép 128 ký tự. (> 128 ký tự)!`);
                    return false;
                }
                return true;
            });

            if (!validNew.length) return prev;

            const allFiles = [...prev, ...validNew];

            return allFiles.filter((file, idx, arr) =>
                idx === arr.findIndex((f) => {
                    if (typeof f === 'string' && typeof file === 'string') return f === file;
                    if (f instanceof File && file instanceof File) {
                        return f.name === file.name && f.size === file.size;
                    }
                    return false;
                })
            );
        });

        e.target.value = null;
    };

    const handleRemoveFile = (file) => {
        setSelectedFiles(prevFiles =>
            prevFiles.filter(item =>
                typeof file === 'string'
                    ? item !== file
                    : !(item instanceof File && item.lastModified === file.lastModified)
            )
        );
    };

    async function processSelectedFiles(files) {
        for (const file of files) {
            const path = await uploadAsyncFunction(file);
            selectFilePathList.current?.push(path);
        }
    }
    async function uploadAsyncFunction(file) {
        return new Promise((resolve) => {
            if (file instanceof File) {
                miscHelper.callApiUploadFile(file).then((mediaList) => {
                    if (_.isArray(mediaList) && mediaList.length > 0) {
                        const firstMedia = _.first(mediaList);
                        const relativePath = firstMedia?.relativePath;
                        if (relativePath) {
                            resolve(relativePath);
                        } else {
                            resolve(null);
                        }
                    } else {
                        resolve(null);
                    }
                });
            } else {
                resolve(file);
            }
        });
    }

    const handleCheckSelectedAgencySale = (agencySale) => {
        if (!!agencySale?.saleId && agencySale?.acceptanceReports?.length) {
            AppDialogHelper.show(
                `Đơn hàng đã tồn tại biên bản nghiệm thu kỹ thuật`,
                `Đơn hàng này đã tồn tại biên bản nghiệm thu kỹ thuật. Vui lòng chọn đơn hàng khác.`,
                [
                    {
                        title: 'Hủy bỏ',
                        type: 'neutral',
                        onClick: () => {
                            AppDialogHelper.hide();
                            return false;
                        },
                    },
                ],
                false,
                AppResource.images.dialogIcons.dialogDeleteVehicle
            );
        } else {
            return true;
        }
    }

    const compareSelectedChargeModelList = (arr1, arr2) => {
        if (arr1?.length !== arr2?.length) return false;
        const countFromArr1 = {};
        arr1.forEach(item => {
            countFromArr1[item.modelId] = (countFromArr1[item.modelId] || 0) + 1;
        });

        const countFromArr2 = {};
        arr2.forEach(item => {
            countFromArr2[item.modelId] = item.quantity;
        });

        const keys1 = Object.keys(countFromArr1).map(Number).sort();
        const keys2 = Object.keys(countFromArr2).map(Number).sort();

        if (keys1.length !== keys2.length || !keys1.every((k, i) => k === keys2[i])) {
            return false;
        }

        return keys1.every(id => countFromArr1[id] === countFromArr2[id]);
    }

    const handleConfirm = async () => {
        if (!selectedChargeStation || !selectedAgencySale || !selectedStaff || !acceptanceDate || !acceptanceNumber || !validFrom) {
            ToastHelper.showError('Vui lòng nhập đầy đủ thông tin');
            return;
        }

        for (const item of selectedChargeModelList ?? []) {
            if (!item?.modelId || !item?.unit || !item?.quantity) {
                ToastHelper.showError('Vui lòng nhập đầy đủ thông tin của sản phẩm');
                return;
            }
        }

        const lists = [
            selectedElectricityList,
            selectedLightElectricityList,
            selectedGroundSystemList,
            selectedPCCCList,
            selectedBuildList,
            selectedOtherList,
        ];

        if (isDigitalType) {
            for (const list of lists) {
                for (const item of list ?? []) {
                    if (!item?.categoryId || !item?.unit || !item?.quantity) {
                        ToastHelper.showError('Vui lòng nhập đầy đủ thông tin của sản phẩm');
                        return;
                    }
                }
            }
        }

        if (!isDigitalType && !paperAcceptanceDocumentFile) {
            ToastHelper.showError('Vui lòng tải lên biên bản scan');
            return;
        }

        if (!_.isEmpty(selectedFiles)) {
            selectFilePathList.current = [];
            await processSelectedFiles(selectedFiles);
        }
        setLoading(true);

        const itemsParam = {
            items: selectedChargeModelList,
            inventoryItems: [
                ...selectedElectricityList,
                ...selectedLightElectricityList,
                ...selectedGroundSystemList,
                ...selectedPCCCList,
                ...selectedBuildList,
                ...selectedOtherList,
            ]
        }

        const param = {
            saleId: selectedAgencySale?.id,
            acceptanceType: AgencyConstants.AcceptanceRole.TECHNICAL,
            documentType: acceptanceType,
            number: acceptanceNumber,
            time: validFrom,
            createdAt: acceptanceDate,
            attachedFiles: selectFilePathList.current,
            document: acceptanceDetail?.document || '',
            items: itemsParam,
            stationId: selectedChargeStation?.id,
            userId: selectedStaff?.id,
        };
        if (acceptanceType === AgencyConstants.AcceptanceType.PAPER) {
            if (paperAcceptanceDocumentFile) {
                param.document = await uploadAsyncFunction(paperAcceptanceDocumentFile);
            }
            param.status = AgencyConstants.AcceptanceStatus.COMPLETED;
        } else {
            if (!acceptanceDetail) {
                param.status = AgencyConstants.AcceptanceStatus.CREATED;
            }
        }

        if (!compareSelectedChargeModelList(selectedAgencySale?.item?.items, selectedChargeModelList) || lists.some(list => list.length > 0)) {
            const paramUpdateSale = {}
            let items = [];
            let inventoryItems = [];
            for (const item of selectedChargeModelList) {
                const quantity = item?.quantity;
                for (let i = 0; i < quantity; i++) {
                    items.push({
                        modelId: item?.modelId,
                        amountBeforeTax: item?.price,
                        taxRate: item?.taxRate,
                        tax: Math.round(item?.price * item?.taxRate),
                        amount: Math.round(item?.price * (1 + item?.taxRate)),
                    });
                }
            }
            for (const list of lists) {
                for (const item of list ?? []) {
                    inventoryItems.push({
                        categoryId: item?.categoryId,
                        name: item?.name,
                        quantity: item?.quantity,
                        taxRate: 0,
                        priceBeforeTax: 0,
                        tax: 0,
                        amountBeforeTax: 0,
                        price: 0,
                        amount: 0,
                        unit: item?.unit,
                    });
                }
            }
            paramUpdateSale.items = items;
            paramUpdateSale.inventoryItems = inventoryItems;
            paramUpdateSale.amountBeforeTax = _.sumBy(selectedChargeModelList, 'amountBeforeTax');
            paramUpdateSale.amount = _.sumBy(selectedChargeModelList, 'amountBeforeTax');
            paramUpdateSale.amountAfterTax = _.sumBy(selectedChargeModelList, 'amount') + saleDetail?.serviceAmount;

            const result = await AgencyHelper.API.requestUpdateSale(saleDetail?.saleId, paramUpdateSale);
            if (!_.isBoolean(result) || result !== true) {
                ToastHelper.showError(result || 'Cập nhật đơn hàng không thành công');
                return;
            }
        }

        await onConfirm(param);
        setLoading(false);
    };

    return (
        <div className="">
            <div style={{ maxHeight: !acceptanceDetail ? '90vh' : '85vh', overflow: 'scroll' }}>
                <div className="d-flex flex-column align-items-center justify-content-center text-center p-5">
                    <span style={UIHelper.buildFontStyle('600', '16px', AppColors.gray[800])}>{title}</span>
                    <span style={UIHelper.buildFontStyle('400', '14px', AppColors.gray[600])}>{contentEl}</span>
                </div>

                {/* chon loai hop dong */}
                <div
                    style={{
                        borderBottom: `1px dashed ${AppColors.gray[200]}`,
                    }}
                >
                    {!defaultAcceptanceType?.length && (
                        <div className="radio-inline w-100 px-5 mb-3">
                            <div className="d-flex align-items-center gap-5">
                                <KTRadio
                                    name="typeAcceptanceElectronic"
                                    label="Biên bản điện tử"
                                    type={KTRadioType.outline}
                                    additionalClassName="radio-success"
                                    onChange={(value) => {
                                        setAcceptanceType(AgencyConstants.AcceptanceType.DIGITAL);
                                    }}
                                    checked={isDigitalType}
                                    disabled={!!acceptanceDetail}
                                />
                                <KTRadio
                                    name="typeAcceptancePaper"
                                    label="Biên bản giấy"
                                    type={KTRadioType.outline}
                                    additionalClassName="radio-success"
                                    onChange={(value) => {
                                        setAcceptanceType(AgencyConstants.AcceptanceType.PAPER);
                                    }}
                                    checked={!isDigitalType}
                                    disabled={!!acceptanceDetail}
                                />
                            </div>
                        </div>
                    )}
                </div>

                <div className='px-6 pb-4'>
                    <div className='row'>
                        <div className={`col-4 px-2 pt-4`}>
                            {UIHelper.buildFormLabel('Trạm sạc', true)}
                            {dropdownChargeStationEl}
                        </div>
                        <div className={`col-4 px-2 pt-4`}>
                            {UIHelper.buildFormLabel('Đơn hàng', true)}
                            {dropdownAgencySaleEl}
                        </div>
                        <div className={`col-4 px-2 pt-4`}>
                            {UIHelper.buildFormLabel('Kỹ thuật viên', true)}
                            {dropdownStaffEl}
                        </div>
                        <div className={`col-4 px-2 pt-4`}>
                            {UIHelper.buildFormLabel('Số biên bản', true)}
                            <KTFormInput
                                name="acceptanceNumber"
                                placeholder="Nhập số biên bản"
                                type={KTFormInputType.text}
                                value={acceptanceNumber}
                                onChange={(value) => setAcceptanceNumber(value)}
                            />
                        </div>
                        <div className={`col-4 px-2 pt-4`}>
                            {UIHelper.buildFormLabel('Ngày biên bản', true)}
                            <KTFormInput
                                name="acceptanceDate"
                                placeholder="Nhập ngày biên bản"
                                type={KTFormInputType.date}
                                value={acceptanceDate}
                                onChange={(value) => setAcceptanceDate(value)}
                            />
                        </div>
                        <div className={`col-4 px-2 pt-4`}>
                            {UIHelper.buildFormLabel('Ngày hiệu lực', true)}
                            <KTFormInput
                                name="acceptanceValidFrom"
                                placeholder="Nhập ngày hiệu lực"
                                type={KTFormInputType.date}
                                value={validFrom}
                                onChange={(value) => setValidFrom(value)}
                                minTime={acceptanceDate}
                            />
                        </div>
                    </div>
                    <div className='pt-4'
                        style={{
                            borderBottom: `1px dashed ${AppColors.gray[200]}`,
                        }}
                    ></div>
                    <div className='py-4'>
                        <div style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[900])}>
                            I. Trụ sạc
                        </div>
                        {chargeModelListTable}
                    </div>
                    {isDigitalType && (
                        <>
                            <div className='pb-4'>
                                <div style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[900])}>
                                    II. Phần cáp điện
                                </div>
                                {electricityListTable}
                            </div>
                            <div className='pb-4'>
                                <div style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[900])}>
                                    III. Phần điện nhẹ (thiết bị, tủ điện, chống sét, aptomat,…)
                                </div>
                                {lightElectricityListTable}
                            </div>
                            <div className='pb-4'>
                                <div style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[900])}>
                                    IV. Hệ thống tiếp địa
                                </div>
                                {groundSystemListTable}
                            </div>
                            <div className='pb-4'>
                                <div style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[900])}>
                                    V. PCCC & Biển nhận diện
                                </div>
                                {pcccListTable}
                            </div>
                            <div className='pb-4'>
                                <div style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[900])}>
                                    VI. Xây dựng & Mặt bằng
                                </div>
                                {buildListTable}
                            </div>
                            <div className='pb-4'>
                                <div style={UIHelper.buildFontStyle('600', '14px', AppColors.gray[900])}>
                                    VII. Chi phí khác
                                </div>
                                {otherListTable}
                            </div>
                        </>
                    )}
                    <div className='row mt-2'>
                        {!isDigitalType && (
                            <div className='col-6 px-2'>
                                {UIHelper.buildFormLabel('Tải lên biên bản scan', true)}
                                <label className="">
                                    {UIHelper.buildNegativeButton(
                                        'Thêm tập tin',
                                        () => { },
                                        <i className="fa-regular fa-upload" />
                                    )}
                                    <input
                                        type="file"
                                        accept={['.pdf']}
                                        onChange={(e) => {
                                            const files = e.target.files;
                                            if (!files || files.length === 0) return;
                                            const file = files[0];
                                            if (file.name.length > 128) {
                                                ToastHelper.showError("Tên file quá dài! Tối đa cho phép 128 ký tự.");
                                                e.target.value = null;
                                                return;
                                            }
                                            setPaperAcceptanceDocumentFile(file);
                                        }}
                                        hidden
                                        onClick={(e) => {
                                            e.target.value = null;
                                        }}
                                    />
                                </label>
                                {paperAcceptanceDocumentFile && (
                                    <div className='col-8'>
                                        <ImageView
                                            name={paperAcceptanceDocumentFile?.lastModified ?? paperAcceptanceDocumentFile}
                                            path={
                                                _.isString(paperAcceptanceDocumentFile)
                                                    ? paperAcceptanceDocumentFile.split('/').pop().replace(/\.pdf-.+$/, '.pdf')
                                                    : paperAcceptanceDocumentFile?.name
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                        <div className='col-6 px-2'>
                            {UIHelper.buildFormLabel('Tải lên tệp tin đính kèm', false)}
                            <div>
                                <div className="">
                                    <label className="">
                                        {UIHelper.buildNegativeButton(
                                            'Thêm tập tin',
                                            () => { },
                                            <i className="fa-regular fa-upload" />
                                        )}
                                        <input
                                            type="file"
                                            accept={['.xls', '.xlsx', '.doc', '.docx', '.pdf']}
                                            onChange={handleFilesChanged}
                                            multiple
                                            hidden
                                            onClick={(e) => {
                                                e.target.value = null;
                                            }}
                                        />
                                    </label>
                                </div>
                                {/* danh sach file dang duoc chon */}
                                {!_.isEmpty(selectedFiles) && (
                                    <div className="row m-0 p-0">
                                        {selectedFiles.map((item, index) => {
                                            return (
                                                <div key={item?.lastModified ?? item ?? index} className='col-8'>
                                                    <ImageView
                                                        name={item?.lastModified ?? item ?? index}
                                                        path={
                                                            _.isString(item)
                                                                ? item.split('/').pop().replace(/\.pdf-.+$/, '.pdf')
                                                                : item?.name
                                                        }
                                                        onRemove={() => {
                                                            handleRemoveFile(item);
                                                        }}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* footer */}
            <div
                className="d-flex align-items-center gap-3 px-4 py-3"
                style={{
                    borderTop: `1px solid ${AppColors.gray[200]}`,
                }}
            >
                {UIHelper.buildNegativeButton('Hủy bỏ', onCancel, null, true)}
                {UIHelper.buildGreenButton('Xác nhận', handleConfirm, null, true, loading)}
            </div>
        </div >
    );
}

export default AgencySaleCreateTechnicalAcceptanceView;
